import { Edit02Icon, Lightning01Icon, PlusIcon, Trash02Icon } from '@systemeio/icons'
import { DigitalProductSection } from 'modules/digital-product/components/digital-product-section'
import { ResourceModal } from 'modules/digital-product/resources/components/resource-modal'
import { ResourceRow } from 'modules/digital-product/resources/components/resource-row'
import { DigitalResourceEditType } from 'modules/digital-product/resources/types/digital-resource-edit-interface'
import { DigitalProductErrorInterface } from 'modules/digital-product/types/digital-product-interface'
import React, { useState } from 'react'
import { Button } from 'shared/components'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twJoin } from 'tailwind-merge'

interface ResourcesSectionProps {
  /** Positional field errors returned by the digital product API. */
  errors?: DigitalProductErrorInterface['resources']
  onChange: (resources: DigitalResourceEditType[]) => void
  /** Renders the list without any action, e.g. a preview of a product picked elsewhere. */
  readOnly?: boolean
  resources: DigitalResourceEditType[]
}

/**
 * Resources of a digital product: the list plus the add / edit / remove flow. Changes are local,
 * the owner decides when to write them to the backend.
 */
export function ResourcesSection({ errors, onChange, readOnly, resources }: ResourcesSectionProps) {
  const { t } = useLocoTranslation()

  const [editedResource, setEditedResource] = useState<DigitalResourceEditType>()
  const [isModalOpened, setIsModalOpened] = useState(false)

  const openModal = (resource?: DigitalResourceEditType) => {
    setEditedResource(resource)
    setIsModalOpened(true)
  }

  const handleSubmit = (resource: DigitalResourceEditType) =>
    onChange(
      editedResource
        ? resources.map(el => (el === editedResource ? resource : el))
        : [...resources, resource],
    )

  const handleDelete = (resource: DigitalResourceEditType) =>
    onChange(resources.filter(el => el !== resource))

  return (
    <>
      <DigitalProductSection
        icon={<Lightning01Icon />}
        title={t('dashboard.funnel.configuration.common.offer_resources.title')}
        description={t('dashboard.digital_product.resources.description')}
        action={
          !readOnly && (
            <Button
              aria-label={t('dashboard.funnel.configuration.offer.add_digital_resource')}
              onClick={() => openModal()}
              variant={'secondary'}
              size={'icon-md'}
            >
              <PlusIcon />
            </Button>
          )
        }
      >
        <div className={twJoin('flex flex-col gap-3', readOnly && 'opacity-50')}>
          {resources.map((resource, index) => (
            <ResourceRow
              key={resource.id ?? index}
              className={readOnly ? 'bg-bg-page' : undefined}
              error={Object.values(errors?.[index] ?? {})
                .flat()
                .join(', ')}
              resource={resource}
              actions={
                !readOnly && (
                  <div className={'flex items-center gap-1'}>
                    <Button
                      aria-label={t('global.edit')}
                      onClick={() => openModal(resource)}
                      variant={'gray'}
                      size={'icon-sm'}
                    >
                      <Edit02Icon />
                    </Button>
                    <Button
                      aria-label={t('global.delete')}
                      onClick={() => handleDelete(resource)}
                      variant={'gray'}
                      size={'icon-sm'}
                    >
                      <Trash02Icon />
                    </Button>
                  </div>
                )
              }
            />
          ))}
        </div>
      </DigitalProductSection>
      <ResourceModal
        opened={isModalOpened}
        resource={editedResource}
        onClose={() => setIsModalOpened(false)}
        onSubmit={handleSubmit}
      />
    </>
  )
}
